import { Injectable, signal, untracked, WritableSignal } from "@angular/core";
import type { NormalizedContentBlock } from "../model/block.types";
import type { PlanData } from "../model/plan.types";

/**
 * Durable store of the last-known `plan` content blocks, keyed by `plan_ref` (doc 31 §3: one
 * block per `plan_ref` per turn and the most recent state wins). It is the join anchor for the
 * SSE live loop: `blackboard_snapshot.plan_run_state` only carries the raw `EngineRunState`,
 * which joins to a stored plan by `plan_id` (doc 31 §7.5) — the plan itself never travels by
 * SSE (doc 31 §3).
 *
 * Mirrors `BlackboardStreamService`: provided per chat-container (NOT root) so it resets with
 * the container, plain Angular signals, `store()/clear()` pattern, no constructor dependencies.
 *
 * Plan blocks are durable per message: `clear()` exists ONLY for agent-switch/teardown
 * symmetry — it must NOT be wired into submit. The dispatcher feeds this store with every
 * normalized block batch; the plan card and the blackboard card consume the stored plans.
 */
@Injectable()
export class PlanBlockStoreService {
  /**
   * Key universe of stored `plan_ref`s — the reactive anchor for "the ref appears/clears".
   * Only bumped when the key set itself changes (new ref inserted, store cleared), so a
   * reader of one existing ref is never re-notified by an unrelated insert (R4f per-key skip).
   */
  readonly #refs = signal<Set<string>>(new Set());

  /** Per-`plan_ref` signal. The container Map is plain (non-reactive): refs add/remove goes
   *  through `#refs`, value writes go through each ref's own signal. */
  readonly #plans = new Map<string, WritableSignal<PlanData | null>>();

  private entry(ref: string, initial?: PlanData | null): WritableSignal<PlanData | null> {
    let sig = this.#plans.get(ref);
    if (!sig) {
      sig = signal<PlanData | null>(initial ?? null);
      this.#plans.set(ref, sig);
    }
    return sig;
  }

  /**
   * Upserts the raw `PlanData` of every `kind === "plan"` block, keyed by `data.plan_ref`
   * (doc 31 §4.2 guarantees `value.id === plan_ref`). Last write per `plan_ref` wins (doc 31
   * §3); dry-run child and parent keep separate entries (distinct `plan_ref`s, doc 31 §6).
   * Non-plan blocks are ignored. The `NormalizedContentBlock` `plan` member carries the raw
   * contract `PlanData` directly, so no cast is needed.
   *
   * R4f hardening (per-key): a ref whose stored value is already the same object is never
   * re-written, and unrelated inserts notify only the refs they actually touch — the
   * dispatcher re-feeds the same snapshot on every turn, and each unconditional whole-map
   * write allocated a fresh Map and notified every tracked reader (the
   * write-on-every-snapshot class that fed the NG0103 loop). Existence changes (first
   * appearance of a ref, `clear()`) go through the `#refs` universe signal so "ref appears
   * later" and teardown stays reactive for readers that previously saw null.
   */
  store(blocks: NormalizedContentBlock[]): void {
    // Architectural rule (NG0103 root cause): a write call must not create a reactive
    // coupling with its caller. The per-ref reads below guard the writes, but called from
    // a reactive context (component effect) they subscribed the caller to the very signals
    // it then wrote — self-notify -> re-run -> NG0103 infinite loop. untracked() removes
    // ONLY that caller-side subscription; store readers keep their reactivity untouched.
    untracked(() => {
      const planBlocks = blocks.filter((block) => block.kind === "plan");
      if (planBlocks.length === 0) return;
      let keyAdded = false;
      for (const block of planBlocks) {
        const plan = block.data;
        const existing = this.#plans.get(plan.plan_ref);
        if (existing) {
          // Existing ref: write only when the value reference changed — notifies just its readers.
          const sameRef = existing() === plan;
          if (!sameRef) existing.set(plan);
        } else {
          // First appearance: create the entry already holding the plan, then bump the universe.
          this.entry(plan.plan_ref, plan);
          keyAdded = true;
        }
      }
      if (keyAdded) this.#refs.set(new Set(this.#plans.keys()));
    });
  }

  /** Stored plan whose `plan_ref` matches, or null (doc 31 §4.2 identity lookup). */
  byPlanRef(planRef: string): PlanData | null {
    const sig = this.#plans.get(planRef);
    if (sig) return sig();
    // Unknown ref: read the universe so a later appearance (or a clear) re-runs this reader.
    this.#refs();
    return null;
  }

  /**
   * Stored plan whose `plan_id` matches, or null — the SSE join lookup: the
   * `blackboard_snapshot` `plan_run_state.plan_id` joins here (doc 31 §7.5). Scans the stored
   * plans; the map is small (a handful of plan blocks per conversation turn). Reads the
   * universe plus every current value, so key-set and revision changes stay reactive.
   */
  byPlanId(planId: string): PlanData | null {
    for (const ref of this.#refs()) {
      const plan = this.#plans.get(ref)?.();
      if (plan?.plan_id === planId) return plan;
    }
    return null;
  }

  /** Current snapshot of all stored plans as an array, in insertion order (deduped by plan_ref). */
  all(): PlanData[] {
    const result: PlanData[] = [];
    for (const ref of this.#refs()) {
      const plan = this.#plans.get(ref)?.();
      if (plan) result.push(plan);
    }
    return result;
  }

  /**
   * Empties the store. For agent-switch/teardown symmetry only — plan blocks are durable per
   * message and are NOT cleared on submit (doc 31 §3). Nulls every ref signal (notifying its
   * readers) and resets the universe so unknown-ref readers re-check.
   */
  clear(): void {
    for (const sig of this.#plans.values()) sig.set(null);
    this.#plans.clear();
    this.#refs.set(new Set());
  }
}
